import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Sparkles, Loader2, Download, Upload, ImageIcon, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BuilderCard, builderLabelStyle, builderHelpStyle } from '@/components/creation-flow/BuilderCard';
import {
  generateBrandImage, knockoutPhoto, fetchMyCreatorPage, updateCreatorPage, fetchMyProducts, updateProduct,
  type GenerateImageParams, type CreatorPage, type CreatorProduct,
} from '@/services/creatorApi';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

type Output = 'banner' | 'card' | 'logo' | 'poster';
type LayoutKey = GenerateImageParams['layout'];
type BgKey = NonNullable<GenerateImageParams['background']>;

const BACKGROUNDS: { key: BgKey; swatch: string; ring: string }[] = [
  { key: 'cream', swatch: '#FCF4E6', ring: '#111827' },
  { key: 'yellow', swatch: '#FAD202', ring: '#111827' },
  { key: 'pink', swatch: '#F46AB9', ring: '#111827' },
  { key: 'blue', swatch: '#4171F9', ring: '#111827' },
  { key: 'ink', swatch: '#111827', ring: '#FAD202' },
];
const LAYOUTS: { key: LayoutKey; label: string }[] = [
  { key: 'text-left-cutout-right', label: 'Logo / photo' },
  { key: 'centered', label: 'Centered' },
  { key: 'text-only', label: 'Text only' },
];

const ImageStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  // Optional deep-link prefill (e.g. "Make a poster" from the series builder):
  // ?output=poster&headline=...&eyebrow=...&cover=<productId>
  const qp = (k: string) => searchParams.get(k) || '';
  const qpOutput = (['banner', 'card', 'logo', 'poster'] as string[]).includes(qp('output')) ? (qp('output') as Output) : 'banner';

  const [output, setOutput] = useState<Output>(qpOutput);
  const [layout, setLayout] = useState<LayoutKey>('text-left-cutout-right');
  const [background, setBackground] = useState<BgKey>(qpOutput === 'poster' ? 'ink' : 'cream');
  const [eyebrow, setEyebrow] = useState(qp('eyebrow') || (qpOutput === 'poster' ? 'NEW SERIES' : 'LIVE WEBINARS & CLASSES'));
  const [headline, setHeadline] = useState(qp('headline') || 'Your name\nhere.');
  const [tagline, setTagline] = useState(qp('tagline') || (qpOutput === 'poster' ? 'New episode weekly' : 'What you teach, in a line'));
  const [monogram, setMonogram] = useState('');
  const [logoKey, setLogoKey] = useState('');
  const [logoIsLight, setLogoIsLight] = useState(false);
  const [cutoutKey, setCutoutKey] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCutout, setUploadingCutout] = useState(false);

  const [generatedKey, setGeneratedKey] = useState('');
  const [generating, setGenerating] = useState(false);
  const [savingTo, setSavingTo] = useState(false);

  const [page, setPage] = useState<CreatorPage | null>(null);
  const [products, setProducts] = useState<CreatorProduct[]>([]);
  const [coverTarget, setCoverTarget] = useState(qp('cover'));

  useEffect(() => {
    fetchMyCreatorPage().then(p => { if (p) { setPage(p); setHeadline(h => h === 'Your name\nhere.' ? p.displayName : h); } }).catch(() => {});
    fetchMyProducts().then(setProducts).catch(() => {});
  }, []);

  // Debounced server render whenever inputs settle.
  const genTimer = useRef<ReturnType<typeof setTimeout>>();
  const runGenerate = useCallback(async () => {
    const heads = headline.split('\n').map(s => s.trim()).filter(Boolean).slice(0, 2);
    if (!heads.length && output !== 'logo') return;
    setGenerating(true);
    try {
      const { key } = await generateBrandImage({ output, layout, background, eyebrow: eyebrow || undefined, headline: heads, tagline: tagline || undefined, logoKey: logoKey || undefined, cutoutKey: cutoutKey || undefined, logoIsLight, monogram: monogram || undefined });
      setGeneratedKey(key);
    } catch (err: any) {
      toast({ title: 'Preview failed', description: err.message, variant: 'destructive' });
    } finally { setGenerating(false); }
  }, [output, layout, background, eyebrow, headline, tagline, logoKey, cutoutKey, logoIsLight, monogram, toast]);

  useEffect(() => {
    clearTimeout(genTimer.current);
    genTimer.current = setTimeout(runGenerate, 700);
    return () => clearTimeout(genTimer.current);
  }, [runGenerate]);

  const uploadImage = async (file: File): Promise<string> => {
    const urlRes = await fetch('/api/get-upload-url', { method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ fileName: file.name, fileType: file.type }) });
    if (!urlRes.ok) throw new Error('Failed to get upload URL');
    const { uploadUrl, s3Key } = await urlRes.json();
    const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
    if (!putRes.ok) throw new Error('Upload failed');
    return s3Key; // bare key — the generator fetches it from hey-joe-videos
  };

  const handleLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingLogo(true);
    try { setLogoKey(await uploadImage(file)); } catch (err: any) { toast({ title: 'Upload failed', description: err.message, variant: 'destructive' }); }
    finally { setUploadingLogo(false); e.target.value = ''; }
  };
  const handleCutout = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingCutout(true);
    try {
      const rawKey = await uploadImage(file);
      // Auto background-removal (BiRefNet). If it fails, use the photo as-is.
      try {
        const { key } = await knockoutPhoto(rawKey);
        setCutoutKey(key);
        toast({ title: 'Background removed' });
      } catch {
        setCutoutKey(rawKey);
        toast({ title: 'Using photo as-is', description: 'Auto background-removal was unavailable.' });
      }
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingCutout(false); e.target.value = ''; }
  };

  const previewSrc = generatedKey ? `/api/s3/${generatedKey}` : '';

  const handleDownload = async () => {
    if (!previewSrc) return;
    const res = await fetch(previewSrc);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${output}-${Date.now()}.png`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveToStore = async () => {
    if (!generatedKey) return;
    setSavingTo(true);
    try {
      if (output === 'logo') {
        if (!page?.displayName) { toast({ title: 'Set up your storefront first', description: 'Add a display name in My Store, then save a logo.' }); return; }
        await updateCreatorPage({ displayName: page.displayName, bio: page.bio, imageUrl: generatedKey, bannerImage: page.bannerImage, slug: page.slug });
        toast({ title: 'Saved as your profile logo' });
      } else if (output === 'banner') {
        if (!page?.displayName) { toast({ title: 'Set up your storefront first', description: 'Add a display name in My Store, then save a banner.' }); return; }
        await updateCreatorPage({ displayName: page.displayName, bio: page.bio, imageUrl: page.imageUrl, bannerImage: generatedKey, slug: page.slug });
        toast({ title: 'Saved as your store banner' });
      } else {
        if (!coverTarget) { toast({ title: 'Pick a listing', description: 'Choose which listing to set this cover on.' }); return; }
        await updateProduct(coverTarget, { coverImage: generatedKey });
        toast({ title: 'Set as the listing cover' });
      }
    } catch (err: any) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally { setSavingTo(false); }
  };

  // Feed the just-generated logo straight into banner mode — no download/reupload.
  const useLogoOnBanner = () => {
    if (!generatedKey) return;
    setLogoKey(generatedKey);
    setLogoIsLight(false); // the generated logo is a full-color tile, not a bare mark
    setOutput('banner');
    toast({ title: 'Logo added — now on a banner' });
  };

  const ghost: React.CSSProperties = { padding: '8px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', minHeight: 38, display: 'inline-flex', alignItems: 'center', gap: 6 };
  const primary: React.CSSProperties = { padding: '9px 18px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', minHeight: 40, display: 'inline-flex', alignItems: 'center', gap: 6 };

  return (
    <main className="min-h-screen" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
      {/* Top bar */}
      <div className="sticky top-0 z-10" style={{ background: 'var(--hj-bg)', borderBottom: '1px solid var(--hj-hairline-soft)' }}>
        <div className="mx-auto flex items-center justify-between gap-3" style={{ maxWidth: 1120, padding: '14px 16px' }}>
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={() => navigate('/my-store')} aria-label="Back to My Store" className="flex-shrink-0 inline-flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 999, background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', cursor: 'pointer' }}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="mono-label flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)' }}><Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--hj-yellow-ink)' }} /> Image studio</div>
              <div className="font-hj-display" style={{ fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700, marginTop: 2 }}>Banner &amp; card creator</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button type="button" onClick={handleDownload} disabled={!generatedKey} style={{ ...ghost, opacity: generatedKey ? 1 : 0.5 }}><Download className="w-3.5 h-3.5" /><span className="hidden sm:inline">Download</span></button>
            <button type="button" onClick={handleSaveToStore} disabled={!generatedKey || savingTo} style={{ ...primary, opacity: (!generatedKey || savingTo) ? 0.6 : 1 }}>{savingTo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}{output === 'banner' ? 'Save to store' : output === 'logo' ? 'Save as logo' : 'Set as cover'}</button>
          </div>
        </div>
      </div>

      <div className="mx-auto" style={{ maxWidth: 1120, padding: '24px 16px 96px' }}>
        <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
          {/* Controls */}
          <div className="w-full lg:w-80 lg:flex-shrink-0 flex flex-col gap-5">
            <BuilderCard kicker="Design">
              <div>
                <Label style={builderLabelStyle}>Output</Label>
                <div className="grid grid-cols-4 gap-1" style={{ padding: 4, borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-beige-deep)' }}>
                  {(['banner', 'card', 'logo', 'poster'] as Output[]).map(o => (
                    <button key={o} type="button" onClick={() => setOutput(o)} style={{ padding: '7px 4px', borderRadius: 8, border: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: output === o ? 700 : 500, background: output === o ? '#fff' : 'transparent', color: 'var(--hj-beige-ink)', boxShadow: output === o ? '0 1px 2px rgb(0 0 0 / 0.08)' : 'none' }}>
                      {o === 'banner' ? 'Banner' : o === 'card' ? 'Card' : o === 'logo' ? 'Logo' : 'Poster'}<br /><span style={{ fontSize: 10, color: 'var(--hj-beige-ink-3)' }}>{o === 'banner' ? '16:5' : o === 'card' ? '4:3' : o === 'logo' ? '1:1' : '9:16'}</span>
                    </button>
                  ))}
                </div>
              </div>
              {output !== 'logo' && output !== 'poster' && (
                <div>
                  <Label style={builderLabelStyle}>Layout</Label>
                  <div className="flex gap-1.5 flex-wrap">
                    {LAYOUTS.map(l => (
                      <button key={l.key} type="button" onClick={() => setLayout(l.key)} style={{ padding: '6px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${layout === l.key ? '#ffd600' : 'var(--hj-beige-line)'}`, background: layout === l.key ? 'var(--hj-yellow-tint)' : '#fff', color: 'var(--hj-beige-ink)' }}>{l.label}</button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label style={builderLabelStyle}>Background</Label>
                <div className="flex gap-2">
                  {BACKGROUNDS.map(b => (
                    <button key={b.key} type="button" onClick={() => setBackground(b.key)} aria-label={b.key} style={{ width: 34, height: 34, borderRadius: 999, background: b.swatch, cursor: 'pointer', border: background === b.key ? `2px solid ${b.ring}` : '1px solid var(--hj-beige-line)', outline: background === b.key ? `2px solid ${b.swatch}` : 'none', outlineOffset: 2 }} />
                  ))}
                </div>
              </div>
            </BuilderCard>

            <BuilderCard kicker="Words">
              {output === 'logo' ? (
                <>
                  <div>
                    <Label style={builderLabelStyle}>Name</Label>
                    <Input value={headline} onChange={e => setHeadline(e.target.value)} placeholder="Your name" />
                    <p style={builderHelpStyle}>We pull your initials from this.</p>
                  </div>
                  <div>
                    <Label style={builderLabelStyle}>Monogram (override)</Label>
                    <Input value={monogram} onChange={e => setMonogram(e.target.value.slice(0, 3))} maxLength={3} placeholder={headline.split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || 'HJ'} />
                    <p style={builderHelpStyle}>1–3 letters. Leave blank to use your initials.</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label style={builderLabelStyle}>Eyebrow</Label>
                    <Input value={eyebrow} onChange={e => setEyebrow(e.target.value)} placeholder="LIVE WEBINARS & CLASSES" />
                  </div>
                  <div>
                    <Label style={builderLabelStyle}>Headline (1–2 lines)</Label>
                    <Textarea value={headline} onChange={e => setHeadline(e.target.value)} rows={2} placeholder={'Your name\nhere.'} />
                  </div>
                  <div>
                    <Label style={builderLabelStyle}>Subhead</Label>
                    <Input value={tagline} onChange={e => setTagline(e.target.value)} placeholder="What you teach, in a line" />
                  </div>
                </>
              )}
            </BuilderCard>

            {output !== 'logo' && (
            <BuilderCard kicker="Art">
              <div>
                <Label style={builderLabelStyle}>Logo (optional)</Label>
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 600 }}>
                      {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}{logoKey ? 'Change' : 'Upload logo'}
                    </span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogo} disabled={uploadingLogo} />
                  </label>
                  {logoKey && <button type="button" onClick={() => setLogoKey('')} style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)', textDecoration: 'underline', background: 'none', border: 0, cursor: 'pointer' }}>Remove</button>}
                </div>
                {logoKey && (
                  <label className="flex items-center gap-2 cursor-pointer" style={{ marginTop: 8 }}>
                    <Switch checked={logoIsLight} onCheckedChange={setLogoIsLight} />
                    <span style={{ fontSize: 12, color: 'var(--hj-beige-ink-2)' }}>Light/white logo (put it on a dark circle)</span>
                  </label>
                )}
              </div>
              <div>
                <Label style={builderLabelStyle}>Cutout photo (optional)</Label>
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 600 }}>
                      {uploadingCutout ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}{uploadingCutout ? 'Removing bg…' : cutoutKey ? 'Change' : 'Upload photo'}
                    </span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleCutout} disabled={uploadingCutout} />
                  </label>
                  {cutoutKey && <button type="button" onClick={() => setCutoutKey('')} style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)', textDecoration: 'underline', background: 'none', border: 0, cursor: 'pointer' }}>Remove</button>}
                </div>
                <p style={builderHelpStyle}>Drop any photo — we remove the background automatically and place it on the blob.</p>
              </div>
            </BuilderCard>
            )}
          </div>

          {/* Preview */}
          <div className="flex-1 min-w-0">
            <div className="mono-label" style={{ color: 'var(--hj-ink-3)', marginBottom: 10 }}>
              Live preview · {output === 'banner' ? 'store banner (16:5)' : output === 'card' ? 'class card (4:3)' : output === 'logo' ? 'logo (1:1)' : 'series poster (9:16)'}
            </div>
            <div className="relative w-full overflow-hidden flex items-center justify-center mx-auto" style={{ aspectRatio: output === 'banner' ? '16 / 5' : output === 'card' ? '4 / 3' : output === 'poster' ? '9 / 16' : '1 / 1', maxWidth: output === 'logo' ? 420 : output === 'poster' ? 300 : undefined, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
              {previewSrc ? <img src={previewSrc} alt="Preview" className="w-full h-full" style={{ objectFit: 'contain' }} /> : null}
              {generating && (
                <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'oklch(0.16 0.005 70 / 0.4)' }}>
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--hj-ink-2)' }} />
                </div>
              )}
            </div>

            {output === 'logo' && (
              <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 14 }}>
                <button type="button" onClick={useLogoOnBanner} disabled={!generatedKey} style={{ ...ghost, opacity: generatedKey ? 1 : 0.5 }}>
                  Use this logo on a banner <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {(output === 'card' || output === 'poster') && (
              <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 14 }}>
                <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Set as cover on</span>
                <div style={{ minWidth: 220 }}>
                  <Select value={coverTarget} onValueChange={setCoverTarget}>
                    <SelectTrigger><SelectValue placeholder="Choose a listing…" /></SelectTrigger>
                    <SelectContent>
                      {products.map(p => <SelectItem key={p._id} value={p._id}>{p.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <p className="m-0" style={{ marginTop: 14, fontSize: 12.5, color: 'var(--hj-ink-3)' }}>
              {output === 'banner'
                ? 'Save it as your storefront hero + marketplace card. Renders at 1600×500.'
                : output === 'card'
                ? 'Use it as a course/webinar cover. Renders at 1600×1200.'
                : output === 'poster'
                ? 'A vertical 9:16 poster for a series cover. Add a cutout for the title character. Renders at 1080×1920.'
                : 'A clean monogram logo for your profile. Renders at 800×800.'}
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ImageStudioPage;
